import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PAYMENT_METHOD_TYPE } from '@woovi/shared';

import type { PaymentActiveWaitingPaymentMethodRoot_charge$key } from './__generated__/PaymentActiveWaitingPaymentMethodRoot_charge.graphql.ts';
import type { PaymentActiveWaitingPaymentMethodRoot_query$key } from './__generated__/PaymentActiveWaitingPaymentMethodRoot_query.graphql.ts';
import PaymentActiveWaitingPaymentMethodCreditCard from './creditCard/PaymentActiveWaitingPaymentMethodCreditCard.tsx';
import PaymentActiveWaitingPaymentMethod from './PaymentActiveWaitingPaymentMethod.tsx';
import PaymentActiveWaitingPaymentMethodPixCreditCard from './pixCreditCard/PaymentActiveWaitingPaymentMethodPixCreditCard.tsx';

type PaymentActiveWaitingPaymentMethodRootProps = {
  query: PaymentActiveWaitingPaymentMethodRoot_query$key;
  charge: PaymentActiveWaitingPaymentMethodRoot_charge$key;
};
const PaymentActiveWaitingPaymentMethodRoot = (
  props: PaymentActiveWaitingPaymentMethodRootProps,
): ReactNode => {
  const query = useFragment<PaymentActiveWaitingPaymentMethodRoot_query$key>(
    graphql`
      fragment PaymentActiveWaitingPaymentMethodRoot_query on Query {
        ...PaymentActiveWaitingPaymentMethodPixCreditCard_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentActiveWaitingPaymentMethodRoot_charge$key>(
    graphql`
      fragment PaymentActiveWaitingPaymentMethodRoot_charge on Charge {
        id
        availablePaymentMethods
        ...PaymentActiveWaitingPaymentMethod_charge
        ...PaymentActiveWaitingPaymentMethodCreditCard_charge
        ...PaymentActiveWaitingPaymentMethodPixCreditCard_charge
      }
    `,
    props.charge,
  );

  const isCreditCard =
    charge.availablePaymentMethods.length === 1 &&
    charge.availablePaymentMethods[0] === PAYMENT_METHOD_TYPE.CREDIT_CARD;

  const isPixCreditCard =
    charge.availablePaymentMethods!.length === 2 &&
    charge.availablePaymentMethods!.includes(PAYMENT_METHOD_TYPE.PIX_COB) &&
    charge.availablePaymentMethods!.includes(PAYMENT_METHOD_TYPE.CREDIT_CARD);

  if (isPixCreditCard) {
    return (
      <PaymentActiveWaitingPaymentMethodPixCreditCard
        query={query}
        charge={charge}
      />
    );
  }

  if (isCreditCard) {
    return <PaymentActiveWaitingPaymentMethodCreditCard charge={charge} />;
  }

  return <PaymentActiveWaitingPaymentMethod charge={charge} />;
};

export default PaymentActiveWaitingPaymentMethodRoot;
